import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, GeoJSON } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './ProjectLocationsMap.scss';

// Fix for default markers in react-leaflet
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const ProjectLocationsMap = () => {
  const [activeFilter, setActiveFilter] = useState('All');
  const [africaGeoJSON, setAfricaGeoJSON] = useState(null);

  // Load Africa GeoJSON data
  useEffect(() => {
    fetch('/map/africa.geo.json')
      .then(response => response.json())
      .then(data => {
        setAfricaGeoJSON(data);
      })
      .catch(error => {});
  }, []);

  // Africa GeoJSON styling for standalone map
  const africaStyle = (feature) => {
    return {
      fillColor: '#ffffff', // White background
      weight: 2,
      opacity: 1,
      color: '#d17c1b', // Orange border
      dashArray: '3,3', // Dashed border
      fillOpacity: 1
    };
  };

  // Function to style Africa GeoJSON
  const onEachFeature = (feature, layer) => {
    if (feature.properties && feature.properties.name) {
      layer.bindPopup(`
        <div class="country-popup">
          <h4>${feature.properties.name}</h4>
          <p>Click markers to view energy projects</p>
        </div>
      `);
    }
  };

  // Sample project data
  const projects = [
    {
      id: 1,
      name: "Noor Ouarzazate Solar Complex",
      country: "Morocco",
      type: "Renewable",
      sector: "Solar Energy",
      investment: "$2.6B",
      coordinates: [31.6295, -7.9811],
      status: "Active"
    },
    {
      id: 2,
      name: "Benban Solar Park",
      country: "Egypt",
      type: "Renewable",
      sector: "Solar Energy",
      investment: "$2.8B",
      coordinates: [24.6877, 32.7186],
      status: "Active"
    },
    {
      id: 3,
      name: "Nigeria Electrification Project",
      country: "Nigeria",
      type: "Access",
      sector: "Grid Extension",
      investment: "$350M",
      coordinates: [9.0765, 7.3986],
      status: "Active"
    },
    {
      id: 4,
      name: "Lake Turkana Wind Project",
      country: "Kenya",
      type: "Renewable",
      sector: "Wind Energy",
      investment: "$700M",
      coordinates: [2.8762, 36.8996],
      status: "Active"
    },
    {
      id: 5,
      name: "Ethiopia-Kenya Power Systems Interconnection",
      country: "Ethiopia",
      type: "Access",
      sector: "Grid Extension",
      investment: "$1.2B",
      coordinates: [9.1450, 40.4897],
      status: "Active"
    },
    {
      id: 6,
      name: "Inga III Hydropower Project",
      country: "DRC",
      type: "Renewable",
      sector: "Hydro Energy",
      investment: "$14B",
      coordinates: [-5.5167, 13.6000],
      status: "Planned"
    },
    {
      id: 7,
      name: "South Africa Renewable Energy Program",
      country: "South Africa",
      type: "Renewable",
      sector: "Solar Energy",
      investment: "$1.5B",
      coordinates: [-30.5595, 22.9375],
      status: "Active"
    }
  ];

  const filters = ['All', 'Renewable', 'Access'];

  const filteredProjects = projects.filter(project => {
    if (activeFilter === 'All') return true;
    return project.type === activeFilter;
  });

  const createCustomIcon = (type) => {
    const color = type === 'Renewable' ? '#4CAF50' : '#FF9800';
    return L.divIcon({
      className: 'custom-marker',
      html: `<div style="
        background-color: ${color};
        width: 20px;
        height: 20px;
        border-radius: 50%;
        border: 3px solid white;
        box-shadow: 0 2px 6px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-size: 10px;
        font-weight: bold;
      ">${type === 'Renewable' ? '☀' : '⚡'}</div>`,
      iconSize: [20, 20],
      iconAnchor: [10, 10]
    });
  };

  return (
    <section className="data-dashboard-projects-section">
      <div className="container">
        <div className="map-container">
          <div className="map-legend-container">
            <div className="leaflet-map-container">
              <MapContainer
                center={[0, 20]}
                zoom={3}
                style={{ height: '500px', width: '100%' }}
                className="project-locations-map"
                zoomControl={true}
                attributionControl={false}
                scrollWheelZoom={false}
                dragging={!window.innerWidth <= 480}
                bounds={[[-37, -20], [40, 55]]}
                boundsOptions={{ padding: [10, 10] }}
              >
                {/* Africa GeoJSON layer with detailed country boundaries */}
                {africaGeoJSON && (
                  <GeoJSON
                    data={africaGeoJSON}
                    style={africaStyle}
                    onEachFeature={onEachFeature}
                  />
                )}
                
                {/* Project markers */}
                {filteredProjects.map(project => (
                  <Marker
                    key={project.id}
                    position={project.coordinates}
                    icon={createCustomIcon(project.type)}
                  >
                    <Popup>
                      <div className="project-details-card">
                        <h3 className="project-name">{project.name}</h3>
                        <div className="project-info">
                          <p><strong>Country:</strong> {project.country}</p>
                          <p><strong>Sector:</strong> {project.sector}</p>
                          <p><strong>Investment:</strong> {project.investment}</p>
                          <p><strong>Status:</strong> {project.status}</p>
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
            
            <div className="map-legend">
              <h3 className="legend-title">Project Types</h3>
              <div className="legend-items">
                <div className="legend-item">
                  <div className="legend-icon renewable"></div>
                  <span>Renewable Energy</span>
                </div>
                <div className="legend-item">
                  <div className="legend-icon access"></div>
                  <span>Energy Access</span>
                </div>
              </div>
              
              <div className="filter-controls">
                <h4 className="filter-title">Filter by Type:</h4>
                <div className="filter-buttons">
                  {filters.map(filter => (
                    <button
                      key={filter}
                      className={`filter-btn ${activeFilter === filter ? 'active' : ''}`}
                      onClick={() => setActiveFilter(filter)}
                    >
                      {filter}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProjectLocationsMap;
